import React, { useMemo } from "react";
import {
  Typography,
  Stack,
  Chip,
  Paper,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Layers as LayersIcon,
  Block as BlockIcon,
  SwapHoriz as SwapIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import InfoIcon from "@/assets/svgs/info.svg";
import { DutyColumn } from "@/services/product-catalogue";

interface DutyStackingPreviewProps {
  columns: DutyColumn[];
}

interface StackingRelation {
  from: string;
  to: string;
  isBidirectional: boolean;
  isValid: boolean;
}

const DutyStackingPreview: React.FC<DutyStackingPreviewProps> = ({ columns }) => {
  // Analyze stacking relationships
  const stackingAnalysis = useMemo(() => {
    const relations: StackingRelation[] = [];
    const exclusionGroups = new Map<string, string[]>();
    const stackableColumns = columns.filter(col => col.isActive && col.isStackable);
    const nonStackableColumns = columns.filter(col => col.isActive && !col.isStackable);

    // Build exclusion groups
    columns.forEach(col => {
      if (col.mutuallyExclusiveGroup) {
        if (!exclusionGroups.has(col.mutuallyExclusiveGroup)) {
          exclusionGroups.set(col.mutuallyExclusiveGroup, []);
        }
        exclusionGroups.get(col.mutuallyExclusiveGroup)!.push(col.name);
      }
    });

    // Analyze bidirectional stacking requirements
    columns.forEach(colA => {
      if (!colA.isActive || !colA.rules) return;

      colA.rules.forEach(ruleA => {
        if (!ruleA.allowedStacksWith || ruleA.allowedStacksWith.length === 0) return;

        ruleA.allowedStacksWith.forEach(allowedAuth => {
          // Find columns with rules that have this authority
          columns.forEach(colB => {
            if (!colB.isActive || colB._id === colA._id || !colB.rules) return;

            colB.rules.forEach(ruleB => {
              if (ruleB.authority === allowedAuth) {
                // Check if B also allows A
                const bAllowsA = ruleB.allowedStacksWith?.some(auth => 
                  colA.rules?.some(r => r.authority === auth)
                );

                relations.push({
                  from: colA.name,
                  to: colB.name,
                  isBidirectional: !!bAllowsA,
                  isValid: !!bAllowsA, // Valid only if bidirectional
                });
              }
            });
          });
        });
      });
    });

    return {
      relations: [...new Map(relations.map(r => [`${r.from}-${r.to}`, r])).values()],
      exclusionGroups,
      stackableColumns,
      nonStackableColumns,
    };
  }, [columns]);

  if (columns.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {/* Column Priority Order */}
      <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Calculation Order (by priority)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          {[...columns]
            .filter(col => col.isActive)
            .sort((a, b) => (a.columnPriority || 10) - (b.columnPriority || 10))
            .map((col, index) => (
              <Chip
                key={col._id}
                label={`${index + 1}. ${col.name} (${col.columnPriority || 10})`}
                size="small"
                variant={col.isStackable ? "filled" : "outlined"}
                color={col.isStackable ? "primary" : "default"}
                icon={col.isStackable ? <LayersIcon /> : <BlockIcon />}
              />
            ))}
        </Stack>
      </Paper>

      {/* Mutual Exclusion Groups */}
      {stackingAnalysis.exclusionGroups.size > 0 && (
        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <BlockIcon sx={{ color: '#ef4444', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Mutual Exclusion Groups
            </Typography>
          </Stack>
          {Array.from(stackingAnalysis.exclusionGroups.entries()).map(([group, cols]) => (
            <Alert 
              key={group} 
              severity="warning" 
              sx={{ mt: 1, py: 0.5 }}
              icon={<BlockIcon />}
            >
              <Typography variant="body2">
                <strong>{group}:</strong> Only one of {cols.join(", ")} can apply
              </Typography>
            </Alert>
          ))}
        </Paper>
      )}

      {/* Stacking Relationships */}
      {stackingAnalysis.relations.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <SwapIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Stacking Relationships
            </Typography>
          </Stack>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {stackingAnalysis.relations.map((rel, index) => (
              <Stack 
                key={index} 
                direction="row" 
                alignItems="center" 
                spacing={1}
                sx={{ 
                  p: 1, 
                  backgroundColor: rel.isValid ? '#dcfce7' : '#fee2e2',
                  borderRadius: 1,
                  border: `1px solid ${rel.isValid ? '#bbf7d0' : '#fecaca'}`
                }}
              >
                {rel.isValid ? (
                  <CheckIcon sx={{ fontSize: 18, color: '#10b981' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                )}
                <Typography variant="body2">
                  {rel.from} → {rel.to}
                </Typography>
                {rel.isBidirectional ? (
                  <Chip 
                    label="Bidirectional" 
                    size="small" 
                    color="success"
                    sx={{ fontSize: '0.7rem' }}
                  />
                ) : (
                  <Tooltip title="This stacking won't work - bidirectional stacking required">
                    <Chip 
                      label="One-way only" 
                      size="small" 
                      color="error"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Stacking Summary */}
      <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Summary
        </Typography>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayersIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
            <Typography variant="body2">
              <strong>{stackingAnalysis.stackableColumns.length}</strong> stackable columns
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BlockIcon sx={{ fontSize: 18, color: '#ef4444' }} />
            <Typography variant="body2">
              <strong>{stackingAnalysis.nonStackableColumns.length}</strong> non-stackable columns
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src={InfoIcon} alt="Info" style={{ width: 18, height: 18 }} />
            <Typography variant="body2">
              <strong>{stackingAnalysis.exclusionGroups.size}</strong> exclusion groups
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Help Text */}
      <Alert severity="info" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Note:</strong> Rules must have bidirectional stacking permissions to stack together. 
          Both rules must explicitly allow each other's authority.
        </Typography>
      </Alert>
    </Stack>
  );
};

export default DutyStackingPreview;